import { useRef, useEffect } from 'react';
import { ERA_ZINDEX as ERAZIndex } from './constants/mapZIndexes';
import { createGoogleMarker, createGoogleMarkerCircle } from './utils';

const ERAColor = '#0A7C5B';
const BLANK_1PX_IMAGE =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

const useDrawERA = (map, data) => {
  const innerMarkerRef = useRef();
  const circleMarkerRef = useRef();

  const handleZoomChange = () => {
    const zoomLevel = map.getZoom();
    if (zoomLevel < 20 && innerMarkerRef.current) {
      innerMarkerRef.current.setMap(null);
    } else if (innerMarkerRef.current && map) {
      innerMarkerRef.current.setMap(map);
    }
  };

  useEffect(() => {
    let listener;
    if (map) {
      listener = map.addListener('zoom_changed', handleZoomChange);
      return () => {
        if (map) {
          try {
            window.google.maps.event.removeListener(listener);
          } catch {
            /* suppress */
          }
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  if (!data) {
    if (innerMarkerRef.current && circleMarkerRef.current) {
      innerMarkerRef.current.setMap(null);
      circleMarkerRef.current.setMap(null);
    }
    return;
  }

  if (!map) {
    return;
  }

  const { data: era } = data;
  const { location } = era;
  const { hunc, latitude, longitude } = location;

  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);
  const position = new window.google.maps.LatLng(lat, lng);

  const innerMarker = createGoogleMarker({
    position,
    label: {
      text: 'Emergency Response Area',
      fontSize: '14px',
      fontWeight: 'bold',
      fontFamily: 'Roboto',
      color: ERAColor,
    },
    icon: {
      className: 'rsos-era-icon',
      url: BLANK_1PX_IMAGE,
    },
    zIndex: ERAZIndex,
  });

  const circleMarker = createGoogleMarkerCircle({
    center: position,
    radius: hunc,
    clickable: false,
    fillColor: ERAColor,
    fillOpacity: 0.15,
    strokeColor: ERAColor,
    strokeWeight: 2,
    strokeOpacity: 1,
    zIndex: ERAZIndex,
  });

  if (!innerMarkerRef.current || !circleMarkerRef.current) {
    innerMarkerRef.current = innerMarker;
    circleMarkerRef.current = circleMarker;
  }

  innerMarkerRef.current.setMap(map);
  circleMarkerRef.current.setMap(map);

  map.panTo(position);
  map.fitBounds(circleMarkerRef.current.getBounds());
  map.setZoom(20);

  return () => {
    innerMarkerRef.current = null;
    circleMarkerRef.current = null;
  };
};

export default useDrawERA;
